import React, { useState, useEffect } from "react";
import {
  Modal,
  Descriptions,
  Tag,
  Space,
  Typography,
  Divider,
} from "antd";
import { getClass } from "../../constants/API/API";
import { ShowToast, Severty } from "../../helper/toast";
import moment from "moment";
import {
  BankOutlined,
  CalendarOutlined,
  ClockCircleOutlined,
  IdcardOutlined,
  NumberOutlined,
} from "@ant-design/icons";

const { Title } = Typography;

const DAYS_OF_WEEK = {
  SUNDAY: "Sunday",
  MONDAY: "Monday",
  TUESDAY: "Tuesday",
  WEDNESDAY: "Wednesday",
  THURSDAY: "Thursday",
  FRIDAY: "Friday",
  SATURDAY: "Saturday",
};

const formatTime = (time) => moment(time, "HH:mm").format("hh:mm A");

const ClassView = ({ viewVisible, setViewVisible, selectedClass: classId }) => {
  const [classData, setClassData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchClassData = async () => {
      if (classId) {
        setLoading(true);
        try {
          const response = await getClass(classId._id);
          if (response.status === 200 && response.data) {
            setClassData(response.data);
          }
        } catch (error) {
          ShowToast(
            error.message || "Failed to fetch class data",
            Severty.ERROR
          );
        } finally {
          setLoading(false);
        }
      }
    };

    fetchClassData();
  }, [classId]);

  const handleCancel = () => {
    setViewVisible(false);
    setClassData(null);
  };

  return (
    <Modal
      open={viewVisible}
      title={<Title level={4} style={{ margin: 0 }}>Class Details</Title>}
      onCancel={handleCancel}
      footer={null}
      width={800}
      destroyOnClose
    >
      {classData && (
        <Descriptions
          bordered
          column={1}
          labelStyle={{
            fontWeight: 500,
            backgroundColor: "#fafafa",
            whiteSpace: "nowrap",
            minWidth: "150px",
          }}
          contentStyle={{
            backgroundColor: "#fff",
          }}
        >
          <Descriptions.Item label={<Space><BankOutlined /> Class Name</Space>}>
            {classData.name}
          </Descriptions.Item>

          <Descriptions.Item label={<Space><BankOutlined /> Branch Name</Space>}>
            {classData.branchId.name}
          </Descriptions.Item>

          <Descriptions.Item label={<Space><ClockCircleOutlined /> Class Status</Space>}>
            <Tag
              color={classData.isActive ? "success" : "error"}
              style={{
                padding: "4px 12px",
                borderRadius: "12px",
                fontSize: "14px",
                width: "max-content"
              }}
            >
              {classData.isActive ? "Active" : "Inactive"}
            </Tag>
          </Descriptions.Item>

          <Descriptions.Item label={<Space><CalendarOutlined /> Class Schedules</Space>}>
            <Space direction="vertical" style={{ width: "100%" }}>
              {classData.schedules.map((schedule, i) => (
                <div
                  key={i}
                  style={{
                    padding: "10px",
                    background: "#f5f5f5",
                    borderRadius: "6px",
                  }}
                >
                  <Tag color="blue" style={{ marginBottom: "8px", width: "max-content" }}>
                    {DAYS_OF_WEEK[schedule.dayOfWeek]}
                  </Tag>

                  {schedule.slots?.length ? (
                    schedule.slots.map((slot, idx) => (
                      <div
                        key={idx}
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          padding: "4px 0",
                          gap: "10px",
                        }}
                      >
                        <span>
                          🕒 {formatTime(slot.startTime)} - {formatTime(slot.endTime)}
                        </span>
                        <Tag color="purple">
                          👤 {slot.trainerId?.name || "N/A"}
                        </Tag>
                      </div>
                    ))
                  ) : (
                    <Tag color="default">No slots available</Tag>
                  )}
                </div>
              ))}
            </Space>
          </Descriptions.Item>

          <Descriptions.Item label={<Space><CalendarOutlined /> Created At</Space>}>
            {moment(classData.createdAt).format("MMMM Do YYYY, h:mm:ss a")}
          </Descriptions.Item>

          <Descriptions.Item label={<Space><CalendarOutlined /> Last Updated</Space>}>
            {moment(classData.updatedAt).format("MMMM Do YYYY, h:mm:ss a")}
          </Descriptions.Item>

          <Descriptions.Item label={<Space><IdcardOutlined /> Class ID</Space>}>
            <Tag style={{ fontFamily: "monospace", fontSize: "13px" }}>
              {classData._id}
            </Tag>
          </Descriptions.Item>

          <Descriptions.Item label={<Space><NumberOutlined /> UID</Space>}>
            <Tag style={{ fontFamily: "monospace", fontSize: "13px" }}>
              {classData.uid}
            </Tag>
          </Descriptions.Item>
        </Descriptions>
      )}
    </Modal>
  );
};

export default ClassView;
